/* Copyright (c) 2020-present CloudIO Inc */

import {
  useStateX,
  useStateXAction,
  useStateXAddToUndo,
  useStateXAtomValue,
  useStateXGetter,
  useStateXSelector,
  useStateXSelectorSetter,
  useStateXSetter,
  useStateXWritableSelector,
} from '@cloudio-saas/statex';
import { ulid } from 'ulid';
import { Divider, IconButton, Menu, MenuItem } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { getManager, Page } from 'componentregistry';
import { isUUID } from 'components/Popup/TreePopup';
import { activeItemSelector } from 'designer/activeItemSelector';
import {
  activeItemIdsAtom,
  activeItemPathAtom,
  designPath,
  pageAtom,
  runtimePath,
} from 'designer/atoms';
import FileSaver from 'file-saver';
import Icon from 'icons/Icon';
import stringify from 'json-stable-stringify';
import { excludeDefaultValues } from 'layout/Sidebar/panels/PropertiesView/property-editors/PropertyEditorUtils';
import { usePlatform } from 'platform/PlatformHooks';
import { getShortenedId, updateItemIDWithinPage } from 'platform/PlatformUtils';
import React from 'react';
import useUser, { isAdministrator } from 'root/useUser';
import { MenuItemPulse } from 'utils/Pulse';
import { maximizeSidebarWidthAtom } from 'utils/state';
import {
  activePinnedSidebarPanelSelector,
  activeSidebarPanelAtom,
  activeSidebarPanelSelector,
  itemExpandedPageLevelAtom,
} from '../../atoms';
import AnalyzeIcon from './AnalyzeIcon';
import { cleanupOrphanItemsAction, TreeType, TreeTypeDesigner } from './state';

const useStyles = makeStyles(() => ({
  menuItem: {
    minWidth: 180,
  },
  icon: {
    marginRight: 12,
  },
}));

type TreeViewProps = {
  treename: TreeType;
  appUid: string;
  pageId: string;
};

function forEachChildId(
  page: Page,
  itemId: string,
  callback: (itm: string) => void,
) {
  const item = page[itemId];
  if (!item) {
    return;
  }
  callback(itemId);
  item.children.forEach((childId) => {
    forEachChildId(page, childId, callback);
  });
}

export default function TreeViewMenu({
  treename,
  appUid,
  pageId,
}: TreeViewProps): React.ReactElement {
  if (!appUid) {
    throw new Error('Missing appUid in TreeViewMenu!');
  }
  const { userName } = useUser();
  const platform = usePlatform();
  const classes = useStyles();
  const ids = useStateXAtomValue(activeItemIdsAtom, {
    params: { appUid, pageId },
  });
  const multipleItemSelected = ids.length > 1;
  const maximized = useStateXAtomValue(maximizeSidebarWidthAtom) === 'Y';
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | undefined>(
    undefined,
  );
  const setActiveItem = useStateXSelectorSetter(activeItemSelector, {
    params: { appUid, treename: TreeTypeDesigner, pageId },
    props: platform,
  });
  const cleanupOrphanItems = useStateXAction(cleanupOrphanItemsAction);
  const [pin, setPin] = useStateXWritableSelector(
    activePinnedSidebarPanelSelector,
    {
      params: { userName },
    },
  );
  const activePanel = useStateXSelector(activeSidebarPanelSelector, {
    params: { appUid },
  });
  const addToUndo = useStateXAddToUndo('io');
  const open = Boolean(anchorEl);
  const get = useStateXGetter();
  const set = useStateXSetter();
  const [runtimeProps, setRuntimeProps] = useStateX(
    runtimePath({ appUid, pageId }),
    {},
  );

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(undefined);
  };

  function expandAll() {
    const page = get(pageAtom, { params: { appUid, pageId } });
    if (page) {
      const obj = Object.keys(page).reduce((_obj, itemId) => {
        _obj[itemId] = true;
        return _obj;
      }, {} as Record<string, boolean>);
      set(itemExpandedPageLevelAtom, obj, {
        params: { treename, appUid, pageId },
      });
    }
    setAnchorEl(undefined);
  }

  function expandAllCurrent() {
    const activeItem = get(activeItemSelector, {
      params: { appUid, treename, pageId },
      props: platform,
    });
    const page = get(pageAtom, { params: { appUid, pageId } });
    if (page && activeItem.itemId) {
      const collect = {
        ...get(itemExpandedPageLevelAtom, {
          params: { treename, appUid, pageId },
        }),
      };
      forEachChildId(page, activeItem.itemId, (i) => {
        collect[i] = true;
      });
      set(itemExpandedPageLevelAtom, collect, {
        params: { treename, appUid, pageId },
      });
    }
    setAnchorEl(undefined);
  }

  function collapseAll() {
    set(
      activeItemSelector,
      { appUid, pageId, itemId: '', expand: false, treename },
      { params: { appUid, treename, pageId }, props: platform },
    );
    set(
      itemExpandedPageLevelAtom,
      {},
      {
        params: { treename, appUid, pageId },
      },
    );
    set(activeItemPathAtom, [], { params: { appUid, pageId, treename } });
    setAnchorEl(undefined);
  }

  function collapseAllCurrent() {
    const activeItem = get(activeItemSelector, {
      params: { appUid, treename, pageId },
      props: platform,
    });
    const page = get(pageAtom, { params: { appUid, pageId } });
    if (page && activeItem.itemId) {
      const collect = {
        ...get(itemExpandedPageLevelAtom, {
          params: { treename, appUid, pageId },
        }),
      };
      forEachChildId(page, activeItem.itemId, (i) => {
        delete collect[i];
      });
      set(itemExpandedPageLevelAtom, collect, {
        params: { treename, appUid, pageId },
      });
    }
    setAnchorEl(undefined);
  }

  function showProperties() {
    set(activeSidebarPanelAtom, 'props');
    setAnchorEl(undefined);
  }

  function saveFile() {
    const page = get(pageAtom, { params: { appUid, pageId } });
    if (!page) return;

    const p = { ...page };
    const itemIds = Object.keys(p);
    const types: string[] = [];
    itemIds.forEach((itemId) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const itm: any = { ...p[itemId] };
      if (!itm.type) {
        console.warn(`Empty item ${itemId}`);
        return;
      }
      const mgr = getManager(itm.type);
      itm.props = excludeDefaultValues(mgr, itm.props);
      p[itemId] = itm;
      types.push(`  '${itemId}': Item<'${mgr.type}'>;`);
    });

    let json = stringify(p, { space: '  ' });
    json = json.replace(/`/g, '\\`');
    json = json.replace(/\${/g, '\\${');

    const blob = new Blob(
      [
        `/* Copyright (c) 2020-present CloudIO Inc */

import { Item } from 'componentregistry';

export const page: {${types.join('\n')}} = ${json};
`,
      ],
      {
        type: 'text/plain;charset=utf-8',
      },
    );
    FileSaver.saveAs(blob, `${pageId}.ts`);
  }

  function handleCustomRegex(json: string): string {
    const formatPlaceholder = '__FORMAT_PLACEHOLDER__';
    json = json.replace(/"([^"]+)":/g, (match, p1) => `${p1}:`);
    json = json.replace(/"(format\([^)]*\))"/g, (match) =>
      match.replace(/"/g, formatPlaceholder),
    );
    json = json.replace(/"(.*?)"/g, (match, p1) =>
      p1 === formatPlaceholder ? formatPlaceholder : `'${p1}'`,
    );
    json = json.replace(new RegExp(formatPlaceholder, 'g'), '"');
    return json;
  }

  function saveJsonFile() {
    const page = get(pageAtom, { params: { appUid, pageId } });
    if (!page) return;

    const p = { ...page };
    const itemIds = Object.keys(p);
    itemIds.forEach((itemId) => {
      const itm: any = { ...p[itemId] };
      if (!itm.type) {
        console.warn(`Empty item ${itemId}`);
        return;
      }
      const mgr = getManager(itm.type);
      itm.props = excludeDefaultValues(mgr, itm.props);
      p[itemId] = itm;
    });

    let json = JSON.stringify(p, null, 2);
    json = handleCustomRegex(json);
    itemIds.slice(1).forEach((itemId) => {
      const regex = new RegExp(`\\b${itemId}\\b:`, 'g');
      json = json.replace(regex, `'${itemId}':`);
    });
    json = json
      .replace(/\\n/g, '\\n')
      .replace(/\\t/g, '\\t')
      .replace(/\\r/g, '\\r')
      .replace(/\\b/g, '\\b')
      .replace(/\\f/g, '\\f');

    const blob = new Blob([`export default ${json};`], {
      type: 'text/plain;charset=utf-8',
    });

    FileSaver.saveAs(blob, `${pageId}.ts`);
  }

  function shortenIds() {
    addToUndo(designPath({ appUid, pageId }), pageId);
    const activeItem = get(activeItemSelector, {
      params: { appUid, treename, pageId },
      props: platform,
    });
    const activeItemId = activeItem?.itemId ?? 'root';
    const page = platform.getter(pageAtom, {
      params: { appUid, pageId },
    });
    let newActiveItemId = activeItemId;
    if (page) {
      let counter = 0;
      let p = { ...page };
      const children = Object.keys(p);
      children.forEach((oldItemId) => {
        if (isUUID(oldItemId)) {
          counter++;
          const newItemId = getShortenedId({
            page: p,
            itemId: oldItemId,
            appUid,
            pageId,
            platform,
          });
          if (oldItemId === activeItemId) {
            newActiveItemId = newItemId;
          }
          p = updateItemIDWithinPage(
            platform,
            p,
            appUid,
            pageId,
            oldItemId,
            newItemId,
          );
        }
      });
      return { counter, page: p, newActiveItemId };
    }
    return { counter: 0, page, newActiveItemId };
  }

  function saveHelpFile() {
    const activeItem = get(activeItemSelector, {
      params: { appUid, treename, pageId },
      props: platform,
    });
    const { page, newActiveItemId } = shortenIds();
    if (!(page && activeItem)) return;
    const p = { ...page };
    const itemIds = Object.keys(p);
    const types: string[] = [];
    itemIds.forEach((itemId) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const itm: any = { ...p[itemId] };
      if (!itm.type) {
        console.warn(`Empty item ${itemId}`);
        return;
      }
      const mgr = getManager(itm.type);
      itm.props = excludeDefaultValues(mgr, itm.props);
      p[itemId] = itm;
      types.push(`  '${itemId}': Item<'${mgr.type}'>;`);
    });

    let json = stringify(p, { space: '  ' });
    json = json.replace(/`/g, '\\`');
    json = json.replace(/\${/g, '\\${');

    let controller = get(['db', 'IoPages', pageId, 'controller']) as
      | string
      | undefined;
    if (controller) {
      controller = controller.replace(/`/g, '\\`');
      controller = controller.replace(/\${/g, '\\${');
    }

    const itm = p[newActiveItemId];
    const mgr = getManager(itm.type);

    const blob = new Blob(
      [
        `/* Copyright (c) 2020-present CloudIO Inc */

// Copy this file to src/componentregistry/components/${mgr.type} folder 
// and import helpRenders into ${mgr.type}Manager.ts file
// import { helpRenders } from './${mgr.type}_Help';

import { ComponentSample, Item } from 'componentregistry';

const page: {${types.join('\n')}} = ${json};

const controller = controller ? \`${controller}\` : undefined;

export const helpRenders: ComponentSample<"${mgr.type}">[] = [{
    title: "!!!This help entry's title!!!",
    description: '!!!Some description about this help entry.!!!',
    page,
    controller,
    exampleId: 'eg-${ulid()}',
    clearParentChildren: true,
    height: 280,
    visibleProps: ${JSON.stringify(Object.keys(itm.props), null, ' ')},
    item: ${JSON.stringify(itm, null, '  ')},
}];
`,
      ],
      {
        type: 'text/plain;charset=utf-8',
      },
    );
    FileSaver.saveAs(blob, `${mgr.type}_Help.ts`);
  }

  const designer = treename === 'designer';

  const menuItems = [];

  menuItems.push(
    <MenuItem key="expand" onClick={expandAll} data-testid="tm-expand-all">
      <Icon className={classes.icon} iconName="expand" />
      Expand All
    </MenuItem>,
  );
  menuItems.push(
    <MenuItem
      key="compress"
      onClick={collapseAll}
      data-testid="tm-collapse-all"
    >
      <Icon className={classes.icon} iconName="compress" />
      Collapse All
    </MenuItem>,
  );
  menuItems.push(<Divider key="edivider" />);
  menuItems.push(
    <MenuItem
      disabled={multipleItemSelected}
      key="expandCurrent"
      onClick={expandAllCurrent}
      data-testid="tm-expand-selected"
    >
      <Icon className={classes.icon} iconName="expand-alt" />
      Expand Selected
    </MenuItem>,
  );
  menuItems.push(
    <MenuItem
      disabled={multipleItemSelected}
      key="compressCurrent"
      onClick={collapseAllCurrent}
      data-testid="tm-collapse-selected"
    >
      <Icon className={classes.icon} iconName="compress-alt" />
      Collapse Selected
    </MenuItem>,
  );
  menuItems.push(<Divider key="sdivider" />);
  if (designer) {
    if (pin !== 'props' && activePanel !== 'props') {
      menuItems.push(
        <MenuItem
          key="showProperties"
          onClick={showProperties}
          data-testid="tm-show-properties"
        >
          <Icon className={classes.icon} iconName="list" />
          Show Properties
        </MenuItem>,
      );
    }
    if (pin === 'tree' && !maximized) {
      menuItems.push(
        <MenuItem
          data-testid="tm-unpin"
          key="Unpin"
          onClick={() => {
            handleClose();
            setPin('HIDDEN');
            set(activeSidebarPanelAtom, 'tree');
          }}
        >
          <Icon className={classes.icon} iconName="thumbtack" />
          Unpin
        </MenuItem>,
      );
    }
    if (pin !== 'tree' && !maximized) {
      menuItems.push(
        <MenuItem
          data-testid="tm-pin"
          key="Pin"
          onClick={() => {
            handleClose();
            setPin('tree');
            set(activeSidebarPanelAtom, 'HIDDEN');
          }}
        >
          <Icon className={classes.icon} iconName="thumbtack" />
          Pin
          <MenuItemPulse
            pulseKey="treePin"
            pulseMessage="Use this option to pin this Page Tree panel to the other side of the screen"
          />
        </MenuItem>,
      );
    }
    menuItems.push(<Divider key="divider" />);
    menuItems.push(
      <MenuItem
        data-testid="tm-clear-all-runtime-props"
        disabled={Object.keys(runtimeProps).length === 0}
        key="clear"
        onClick={() => {
          handleClose();
          setRuntimeProps({});
        }}
      >
        <Icon className={classes.icon} iconName="times" />
        Clear All Runtime Property Values
      </MenuItem>,
    );
    if (isAdministrator(platform, 'cloudio')) {
      menuItems.push(
        <MenuItem
          data-testid="tm-download-page-md"
          key="download-page"
          onClick={() => {
            handleClose();
            saveFile();
          }}
        >
          <Icon className={classes.icon} iconName="download" />
          Download Page Metadata
        </MenuItem>,
      );
    }
    if (isAdministrator(platform, 'cloudio')) {
      menuItems.push(
        <MenuItem
          data-testid="tm-download-page-md-processed"
          key="download-page-processed"
          onClick={() => {
            handleClose();
            saveJsonFile();
          }}
        >
          <Icon className={classes.icon} iconName="file-export" />
          Download Page Metadata (Processed)
        </MenuItem>,
      );
    }
    menuItems.push(
      <MenuItem
        data-testid="tm-download-help"
        key="download-help"
        onClick={() => {
          handleClose();
          saveHelpFile();
        }}
      >
        <Icon className={classes.icon} iconName="question" />
        Download Help Metadata
      </MenuItem>,
    );
    menuItems.push(
      <MenuItem
        data-testid="tm-cleanup"
        key="cleanup"
        onClick={() => {
          handleClose();
          addToUndo(designPath({ appUid, pageId }), pageId);
          cleanupOrphanItems({ platform, appUid, pageId });
        }}
      >
        <Icon className={classes.icon} iconName="broom" />
        Cleanup Orphan Items
      </MenuItem>,
    );
    menuItems.push(
      <MenuItem
        data-testid="tm-shortenAllIds"
        key="shortenAllIds"
        onClick={() => {
          handleClose();
          const { counter, page, newActiveItemId } = shortenIds();
          if (counter) {
            platform.setter(pageAtom, page, { params: { appUid, pageId } });
            platform.showSuccess(`Shortened ${counter} Item IDs!`);
            setActiveItem({
              appUid,
              pageId,
              itemId: newActiveItemId,
              expand: true,
              treename: TreeTypeDesigner,
            });
          } else {
            platform.showInfo('Found no IDs eligible to shorten!');
          }
        }}
      >
        <Icon className={classes.icon} iconName="wand-magic" />
        Shorten All Item IDs
      </MenuItem>,
    );
  }

  return (
    <>
      {designer ? (
        <AnalyzeIcon appUid={appUid} platform={platform} pageId={pageId} />
      ) : null}
      <IconButton
        data-testid="tree-menu"
        color="primary"
        aria-label="display more actions"
        onClick={handleClick}
      >
        <span aria-hidden="false" style={{ display: 'none' }}>
          More Actions
        </span>
        <Icon iconName="ellipsis-v" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        keepMounted
        open={open}
        onClose={handleClose}
        BackdropProps={{ 'aria-label': 'Menu Backdrop' }}
      >
        {menuItems}
      </Menu>
    </>
  );
}
